import { getData } from './ajax-utils';

/**
 * Convert string to blob
 * 
 * @param {string} dataURI 
 * 
 * @return {Blob}
 */
export function dataURItoBlob(dataURI: string) : Blob {
    // convert base64 to raw binary data held in a string
    const byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const _ia = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
        _ia[i] = byteString.charCodeAt(i);
    }

    const dataView = new DataView(arrayBuffer);
    const blob = new Blob([dataView], { type: mimeString });
    return blob;
}

/**
 * Check if current user is logged in
 * 
 * @return {Promise<boolean>}
 */
export async function checkIfUserIsLoggedIn(): Promise<boolean> {
    return await getData(scriptVar.ajaxUrl, { action: 'check_if_logged_in' }).then((resp: WpAjaxResponseType) => resp.success);
}

/**
 * Scroll into an element if he is not fully visible
 * 
 * @param {HTMLElement} $el
 * @param {HTMLElement} $container
 * 
 * @return {void}
 */
export function scrollToElementIfNotVisible($el: HTMLElement, $container: HTMLElement): void {
    if ($el.offsetTop < $container.scrollTop) {
        $container.scrollTop = $el.offsetTop;
    } else {
        const offsetBottom = $el.offsetTop + $el.offsetHeight;
        const scrollBottom = $container.scrollTop + $container.offsetHeight;
        if (offsetBottom > scrollBottom) {
            $container.scrollTop = offsetBottom - $container.offsetHeight;
        }
    }
}

/**
 * Validate a number with the Luhn Algorithm
 * 
 * @param {string|number} number
 * 
 * @return {boolean}
 */
export function validateLuhn(number: string|number) : boolean {
    const trimmed = String(number).replace(/[\s]/g, '');
    const length = trimmed.length;
    let odd = false;
    let total = 0;
    let calc = 0;
    let calc2 = 0;

    if (!/^[0-9]+$/.test(trimmed)) {
        return false;
    }

    for (let i = length; i > 0; i--) {
        calc = parseInt(trimmed.charAt(i - 1));
        if (!odd) {
            total += calc;
        } else {
            calc2 = calc * 2;

            switch (calc2) {
                case 10: calc2 = 1; break;
                case 12: calc2 = 3; break;
                case 14: calc2 = 5; break;
                case 16: calc2 = 7; break;
                case 18: calc2 = 9; break;
            }
            total += calc2;
        }
        odd = !odd;
    }

    return (total !== 0 && (total % 10) === 0);
}

/**
 * Format a number to a locale string price
 * 
 * @param {number} number
 * 
 * @return {string}
 */
export function formatPrice(number: number) : string {
    return number.toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

/**
 * Return an uniqid
 * 
 * @param {string}  prefix
 * @param {boolean} random
 * 
 * @return {string}
 */
export function uniqid(prefix: string = '', random: boolean = false) : string {
    const sec = (Date.now() * 1000) + (Math.random() * 1000);
    const id = sec.toString(16).replace(/\./g, '').padEnd(14, '0');
    return `${prefix}${id}${random ? `.${Math.trunc(Math.random() * 100000000)}` : ''}`;
}

/**
 * Transforme un texte CamelCase en hyphen-case
 * 
 * @param {string} string 
 * 
 * @return {string}
 */
export function camelToHyphenCase(string: string): string {
    return string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Transforme un texte CamelCase en snake_case
 * 
 * @param {string} string 
 * 
 * @return {string}
 */
export function camelToSnakeCase(string: string): string {
    return string.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
}

/**
 * Set a field error
 * 
 * @param {Element} $errorField 
 * @param {string|false} errorMessage 
 * 
 * @return {void}
 */
export function setFieldError($errorField: HTMLElement, errorMessage: string|false): void {
    if (errorMessage) {
        $errorField.innerHTML = `<li>${errorMessage}</li>`;
        $errorField.closest('.form-group')?.classList.add('has-error');
    } else {
        $errorField.innerHTML = '';
        $errorField.closest('.form-group')?.classList.remove('has-error');
    }
}

/**
 * Remove all errors into $container
 * 
 * @param {HTMLElement} $container
 * 
 * @return {void}
 */
export function removeFieldErrors($container: HTMLElement): void {
    $container.querySelectorAll('.form-error').forEach($errorContainer => {
        $errorContainer.innerHTML = '';
        
        const $formGroup = $errorContainer.closest('.form-group');
        if ($formGroup) {
            $formGroup.classList.remove('has-error');
        }
    });
}
